import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  type CardReconcileManyResult,
  formatMoney,
  fromCents,
  type InvoiceListItem,
  type InvoicesResponse,
  italianToday,
  type SelfBillingProposal,
  toCents,
} from '@k-management/shared';
import { useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { useCan } from '../../auth/AuthProvider.js';
import { usePageTitle } from '../../components/layout/page-chrome.js';
import { apiRequest } from '../../lib/api.js';
import { formatDate } from '../../lib/format.js';
import { treasuryKeys } from './treasury-ui.js';

/** What the payment of a self-billing is, and the words of the list. */
function paymentLabel(item: InvoiceListItem, proposal: SelfBillingProposal | undefined) {
  if (item.state === 'saldata') return <span className="ok">✓ collegata</span>;
  if (!proposal) return <span className="error">da collegare</span>;
  const difference = toCents(proposal.amount) - toCents(item.residual);
  return (
    <span className="warn">
      proposta: {proposal.description} del {formatDate(proposal.date)},{' '}
      {formatMoney(proposal.amount)}
      {difference !== 0 && (
        <small className="block">cambio {formatMoney(fromCents(difference))}</small>
      )}
    </span>
  );
}

/**
 * Tesoreria › Autofatture (3.17): the self-billing of foreign suppliers. They
 * are issued in Gestify; here each is paid for its taxable and linked to the
 * spending that paid it, which keeps its item of the plan.
 */
export function SelfBillingPage() {
  usePageTitle('Autofatture');
  const queryClient = useQueryClient();
  const canCards = useCan('tesoreria.cards');
  const canWrite = useCan('tesoreria.write', 'tesoreria.cards');
  const [params, setParams] = useSearchParams();
  const current = Number(italianToday().slice(0, 4));
  const year = Number(params.get('anno') ?? current - 2);
  const [only, setOnly] = useState<'all' | 'open'>('open');
  const invoices = useQuery({
    queryKey: treasuryKeys.invoices,
    queryFn: () => apiRequest<InvoicesResponse>('/treasury/invoices'),
  });
  const proposals = useQuery({
    queryKey: ['treasury', 'self-billing', year],
    queryFn: () =>
      apiRequest<SelfBillingProposal[]>(`/treasury/cards/self-billing-proposals?year=${year}`),
    enabled: canCards,
  });
  const confirm = useMutation({
    mutationFn: () =>
      apiRequest<CardReconcileManyResult>('/treasury/cards/movements/reconcile', {
        method: 'POST',
        body: {
          items: (proposals.data ?? []).map((item) => ({
            movementId: item.movementId,
            request: item.request,
          })),
        },
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['treasury'] }),
  });

  const all = (invoices.data?.invoices ?? []).filter(
    (item) =>
      item.selfBilling &&
      item.direction === 'ricevuta' &&
      item.kind === 'fattura' &&
      item.date.startsWith(String(year)),
  );
  const open = all.filter((item) => item.state !== 'saldata');
  const rows = only === 'open' ? open : all;
  const proposalOf = new Map((proposals.data ?? []).map((item) => [item.invoiceId, item]));
  const sum = (items: readonly InvoiceListItem[]) =>
    fromCents(items.reduce((total, item) => total + toCents(item.netDue), 0));
  const years = Array.from({ length: current - 2022 }, (_, index) => current - index);

  return (
    <section className="stack">
      <p className="muted small">
        Le autofatture dei fornitori esteri si emettono in Gestify. Qui si pagano per l’imponibile,
        perché l’IVA è a debito e a credito insieme, e si collegano alla spesa con carta o al
        bonifico che le ha pagate.
      </p>
      <div className="kpi-row">
        <div className="card kpi">
          <span className="muted small">Autofatture {year}</span>
          <strong>{all.length}</strong>
          <span className="muted small">imponibile {formatMoney(sum(all))}</span>
        </div>
        <div className="card kpi">
          <span className="muted small">Da collegare</span>
          <strong className={open.length ? 'error' : undefined}>{open.length}</strong>
          <span className="muted small">imponibile {formatMoney(sum(open))}</span>
        </div>
      </div>
      <div className="row between">
        <div className="row">
          <label>
            Anno{' '}
            <select value={year} onChange={(event) => setParams({ anno: event.target.value })}>
              {years.map((item) => (
                <option key={item} value={item}>
                  {item}
                </option>
              ))}
            </select>
          </label>
          <label>
            Pagamento{' '}
            <select
              value={only}
              onChange={(event) => setOnly(event.target.value as 'all' | 'open')}
            >
              <option value="open">Da collegare</option>
              <option value="all">Tutte</option>
            </select>
          </label>
        </div>
        {canWrite && (proposals.data?.length ?? 0) > 0 && (
          <button
            type="button"
            className="primary"
            disabled={confirm.isPending}
            onClick={() => confirm.mutate()}
          >
            {confirm.isPending
              ? 'Attendere…'
              : `Conferma gli abbinamenti proposti (${proposals.data!.length})`}
          </button>
        )}
      </div>
      {confirm.error && <p className="error">{confirm.error.message}</p>}
      {confirm.data && (
        <p className="card small">
          Collegate {confirm.data.done}
          {confirm.data.failed.length > 0 &&
            `; non riuscite ${confirm.data.failed.length}: ${[
              ...new Set(confirm.data.failed.map((item) => item.error)),
            ].join('; ')}`}
          .
        </p>
      )}
      {invoices.error && <p className="error">{invoices.error.message}</p>}
      {proposals.error && <p className="error">{proposals.error.message}</p>}
      <div className="table-wrap">
        <table className="data-table">
          <thead>
            <tr>
              <th>Numero</th>
              <th>Data</th>
              <th>Fornitore</th>
              <th className="numeric">Imponibile</th>
              <th className="numeric">IVA</th>
              <th>Pagamento</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((item) => (
              <tr key={item.id}>
                <td>
                  <Link to={`/tesoreria/fatture/${item.id}`}>{item.number}</Link>
                </td>
                <td>{formatDate(item.date)}</td>
                <td>{item.counterpartyName}</td>
                <td className="numeric">{formatMoney(item.netDue)}</td>
                <td className="numeric">
                  {formatMoney(fromCents(toCents(item.total) - toCents(item.netDue)))}
                </td>
                <td>{paymentLabel(item, proposalOf.get(item.id))}</td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr>
                <td colSpan={6} className="muted">
                  Nessuna autofattura {only === 'open' ? 'da collegare ' : ''}nel {year}.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}
